import React, { useEffect, useMemo, useState } from 'react'
import { ArrowUpDown, RotateCw } from 'lucide-react'

import { nutrimaticDevice, nutrimaticLabels } from '@/nutrimatic'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { getActiveChainId } from '@/config/active'
import { SpotQuoteDisplay, SpotRateDisplay } from '@/ui/dex/spot-quote-display'
import type { PoolIdentity } from '@/data/marketApi/types'
import { cn } from '@/lib/utils'
import type { Currency } from '../../../sdk'
import {
  getBaseQuoteSpotRate,
  getDirectedSpotQuote,
  getDirectedSpotRate,
  type OnChainPairData,
} from '../../../features/market/useOnChainPairData'

const USD_PRIMARY_AMOUNT_SIZE = {
  md: 'text-base font-semibold',
  lg: 'text-xl font-semibold sm:text-2xl',
  xl: 'text-2xl font-semibold sm:text-3xl',
} as const

function SpotQuoteIdle({ message = '—' }: { message?: string }) {
  return <span className="font-mono text-xs text-muted-foreground/70">{message}</span>
}

function useSpotRate(
  pairData: OnChainPairData,
  pay: Currency | null | undefined,
  receive: Currency | null | undefined,
) {
  const chainId = getActiveChainId()
  return useMemo(
    () => getDirectedSpotRate(pairData, pay, receive, chainId),
    [chainId, pairData, pay, receive],
  )
}

/** Precio spot principal — cabecera superior derecha, dirección fija del trade (From → To). */
export function SwapNutrimaticReadoutSpotRateHeader({
  pairData,
  pay,
  receive,
  className,
  size = 'lg',
}: {
  pairData: OnChainPairData
  pay: Currency | null | undefined
  receive: Currency | null | undefined
  className?: string
  size?: 'md' | 'lg' | 'xl'
}) {
  const t = useTranslation()
  const spotRate = useSpotRate(pairData, pay, receive)

  if (pairData.loading) {
    return <SpotQuoteIdle />
  }

  if (!pay || !receive) {
    return <SpotQuoteIdle />
  }

  if (!pairData.exists) {
    return <SpotQuoteIdle message={t.common.noPool} />
  }

  if (!spotRate) {
    return <SpotQuoteIdle />
  }

  return (
    <div className={cn(nutrimaticDevice.readoutSpotQuote, className)}>
      <SpotRateDisplay amount={spotRate.amount} unitSymbol={spotRate.unitSymbol} size={size} />
    </div>
  )
}

/** Precio spot del activo base en unidad quote (índice de mercado; no invierte con el swap). */
export function SwapNutrimaticReadoutBasePriceHeader({
  pairData,
  indexedPool,
  marketPrice,
  basePriceUsd,
  className,
  size = 'lg',
}: {
  pairData: OnChainPairData
  indexedPool?: PoolIdentity
  marketPrice: string
  /** Precio USD Nutrimatic del base (`$…`); opcional / `—` si no hay ancla. */
  basePriceUsd?: string | null
  className?: string
  size?: 'md' | 'lg' | 'xl'
}) {
  const t = useTranslation()
  const chainId = getActiveChainId()
  const spotRate = useMemo(() => {
    if (!indexedPool?.baseToken || !indexedPool?.quoteToken) return null
    const { baseToken: base, quoteToken: quote } = indexedPool
    // Preferir on-chain (misma convención que el footer: quote por 1 base).
    // marketPrice Nutrimatic ya es quote-per-base — usarlo as-is, sin
    // formatBaseQuoteSpotPrice (esa helper asume crudo token1/token0).
    const onChain = getBaseQuoteSpotRate(pairData, base, quote, chainId)
    if (onChain) return onChain
    if (marketPrice && marketPrice !== '—') {
      return { amount: marketPrice, unitSymbol: quote.symbol }
    }
    return null
  }, [chainId, indexedPool, marketPrice, pairData])

  const hasUsd = Boolean(basePriceUsd && basePriceUsd !== '—')
  const [usdPrimary, setUsdPrimary] = useState(true)

  useEffect(() => {
    setUsdPrimary(true)
  }, [indexedPool?.pairAddress, indexedPool?.baseToken?.address, indexedPool?.quoteToken?.address])

  if (pairData.loading) {
    return <SpotQuoteIdle />
  }

  if (!indexedPool?.baseToken || !indexedPool?.quoteToken) {
    return <SpotQuoteIdle />
  }

  if (!pairData.exists) {
    return <SpotQuoteIdle message={t.common.noPool} />
  }

  if (!spotRate) {
    return <SpotQuoteIdle />
  }

  const showUsdPrimary = hasUsd && usdPrimary
  const pairSecondary = nutrimaticLabels.pairQuoteSecondary(spotRate.amount, spotRate.unitSymbol)
  const usdSecondary = hasUsd ? nutrimaticLabels.baseUsdPrice(basePriceUsd!) : null

  const displayToggle = hasUsd ? (
    <button
      type="button"
      className={nutrimaticDevice.readoutSpotQuoteDisplayToggle}
      onClick={() => setUsdPrimary((prev) => !prev)}
      aria-label={nutrimaticLabels.swapPriceDisplayToggle}
      aria-pressed={usdPrimary}
      title={nutrimaticLabels.swapPriceDisplayToggle}
    >
      <ArrowUpDown className="size-2.5" aria-hidden />
    </button>
  ) : null

  return (
    <div className={cn(nutrimaticDevice.readoutSpotQuote, className)}>
      <div className={nutrimaticDevice.readoutSpotQuoteLines}>
        {showUsdPrimary ? (
          <>
            <span
              className={cn(
                'font-mono leading-none',
                nutrimaticDevice.readoutSpotQuoteUsdPrimary,
                USD_PRIMARY_AMOUNT_SIZE[size],
              )}
              aria-label={nutrimaticLabels.baseUsdPriceAria}
            >
              {basePriceUsd}
            </span>
            <div className={nutrimaticDevice.readoutSpotQuoteSecondaryRow}>
              <span className={nutrimaticDevice.readoutSpotQuoteSecondary}>{pairSecondary}</span>
              {displayToggle}
            </div>
          </>
        ) : (
          <>
            <SpotRateDisplay amount={spotRate.amount} unitSymbol={spotRate.unitSymbol} size={size} />
            {usdSecondary ? (
              <div className={nutrimaticDevice.readoutSpotQuoteSecondaryRow}>
                <span
                  className={nutrimaticDevice.readoutSpotQuoteSecondary}
                  aria-label={nutrimaticLabels.baseUsdPriceAria}
                >
                  {usdSecondary}
                </span>
                {displayToggle}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Línea de conversión spot del mercado — pie de consola.
 * Orden canónico = base/quote Nutrimatic; el botón local invierte solo la visualización.
 * No sigue el sentido del trade (handleSwitchTokens).
 */
export function SwapNutrimaticReadoutSpotQuoteFooter({
  pairData,
  /** @deprecated Prefer `base` / `quote` market legs. */
  pay,
  /** @deprecated Prefer `base` / `quote` market legs. */
  receive,
  base,
  quote,
  className,
}: {
  pairData: OnChainPairData
  pay?: Currency | null
  receive?: Currency | null
  base?: Currency | null
  quote?: Currency | null
  className?: string
}) {
  const t = useTranslation()
  const chainId = getActiveChainId()
  const [showInverted, setShowInverted] = useState(false)

  // Prefer explicit market legs; do not fall back to trade pay/receive (those flip on switch).
  const marketBase = base ?? null
  const marketQuote = quote ?? null
  const spotPay = showInverted ? marketQuote : marketBase
  const spotReceive = showInverted ? marketBase : marketQuote

  const spotQuote = useMemo(
    () => getDirectedSpotQuote(pairData, spotPay, spotReceive, chainId),
    [chainId, pairData, spotPay, spotReceive],
  )

  // Keep legacy props referenced so call sites migrating off pay/receive still typecheck.
  void pay
  void receive

  if (pairData.loading) {
    return (
      <div className={cn(nutrimaticDevice.readoutSpotQuoteFooter, className)}>
        <SpotQuoteIdle />
      </div>
    )
  }

  if (!marketBase || !marketQuote || !pairData.exists || !spotQuote) {
    return (
      <div className={cn(nutrimaticDevice.readoutSpotQuoteFooter, className)}>
        <SpotQuoteIdle
          message={!pairData.exists && marketBase && marketQuote ? t.common.noPool : '—'}
        />
      </div>
    )
  }

  return (
    <div className={cn(nutrimaticDevice.readoutSpotQuoteFooter, className)}>
      <div className={nutrimaticDevice.readoutSpotQuoteDetail}>
        <SpotQuoteDisplay
          paySymbol={spotQuote.paySymbol}
          rateAmount={spotQuote.rateAmount}
          rateUnitSymbol={spotQuote.rateUnitSymbol}
          size="sm"
        />
        <button
          type="button"
          className={cn(nutrimaticDevice.physicalButton, nutrimaticDevice.readoutSpotQuoteInvert)}
          onClick={() => setShowInverted((prev) => !prev)}
          aria-label={t.common.invertMarketQuoteRef}
          aria-pressed={showInverted}
          title={t.common.invertMarketRef}
        >
          <RotateCw className="size-3" aria-hidden />
        </button>
      </div>
    </div>
  )
}
